import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, RotateCcw, CheckCircle2, AlertCircle, MapPin, Tag, Shield, Sparkles, FileText, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDateTime, relativeTime } from "@/lib/utils-app";
import { exportTillsynPdf, inferDocumentKind } from "@/lib/tillsynPdf";
import TillsynInspection from "@/components/TillsynInspection";
import { toast } from "sonner";

const STATUS_BADGE = {
  under_utredning: { label: "Under utredning", classes: "bg-blue-50 text-blue-700 border-blue-200" },
  kommunicering: { label: "Kommunicering", classes: "bg-amber-50 text-amber-700 border-amber-200" },
  beslut: { label: "Beslut", classes: "bg-violet-50 text-violet-700 border-violet-200" },
  uppfoljning: { label: "Uppföljning", classes: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  avslutat: { label: "Avslutat", classes: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function TillsynDetail() {
  const { tillsyn_id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setCase] = useState(null);
  const [step, setStep] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const load = async () => {
    try {
      const [c1, s1] = await Promise.all([
        api.get(`/tillsyn/${tillsyn_id}`),
        api.get(`/tillsyn/${tillsyn_id}/step`),
      ]);
      setCase(c1.data); setStep(s1.data);
    } catch (err) {
      toast.error("Kunde inte hämta ärende");
      nav("/tillsyn");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tillsyn_id]);

  const decide = async (value) => {
    if (!step || step.terminal) return;
    setSubmitting(true);
    try {
      await api.post(`/tillsyn/${tillsyn_id}/decision`, { step: step.step, value, note: note || null });
      setNote("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte spara beslut");
    } finally { setSubmitting(false); }
  };

  const reset = async () => {
    if (!window.confirm("Vill du nollställa alla beslut och börja om från steg 1?")) return;
    try {
      await api.post(`/tillsyn/${tillsyn_id}/reset`);
      toast.success("Återställt");
      await load();
    } catch { toast.error("Kunde inte återställa"); }
  };

  if (!c) return <div className="flex items-center justify-center min-h-[60vh] text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const status = STATUS_BADGE[c.status] || STATUS_BADGE.under_utredning;
  const progress = c.outcome ? 100 : Math.min(Math.round(((c.current_step) / 15) * 100), 95);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-fade-in-up">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <button onClick={() => nav("/tillsyn")} data-testid="back-tillsyn"
          className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900">
          <ArrowLeft className="w-4 h-4" /> Tillbaka till listan
        </button>
        <button onClick={() => setPdfOpen(true)} data-testid="export-pdf-btn"
          className="inline-flex items-center gap-2 text-xs font-medium px-3 py-2 border border-zinc-300 hover:border-blue-700 hover:bg-blue-50 hover:text-blue-700 rounded-sm transition">
          <FileText className="w-3.5 h-3.5" /> Exportera beslut som PDF
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">{c.ref}</div>
            <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>{c.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-zinc-600">
              {c.address && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {c.address}</span>}
              {c.fastighet && <span>{c.fastighet}</span>}
              <span className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {c.category}</span>
              <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Källa: {c.source}</span>
            </div>
          </div>
          <span className={`px-2.5 py-1 text-xs font-medium border rounded-sm ${status.classes}`}>{status.label}</span>
        </div>

        {c.description && (
          <p className="mt-4 text-sm text-zinc-700 leading-relaxed">{c.description}</p>
        )}

        {/* Progress bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-zinc-500 mb-1.5">
            <span className="font-semibold">{c.outcome ? "Avslutat" : `Steg ${c.current_step} av 15`}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
            <div className={`h-full transition-all duration-500 ${c.outcome ? "bg-emerald-600" : "bg-blue-700"}`}
                 style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* Decision wizard */}
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {step?.terminal ? (
            <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-6" data-testid="tillsyn-outcome">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-700 stroke-[1.5]" />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Slutgiltigt utfall</div>
                  <div className="mt-1 text-2xl font-semibold text-emerald-900" style={{ fontFamily: "Outfit" }}>
                    {step.outcome_label}
                  </div>
                </div>
              </div>
              <button onClick={reset} data-testid="reset-tillsyn"
                className="mt-6 inline-flex items-center gap-2 border border-emerald-300 hover:bg-white text-emerald-800 px-3 py-2 rounded-sm text-xs font-medium">
                <RotateCcw className="w-3.5 h-3.5" /> Återöppna och börja om
              </button>
            </div>
          ) : step?.meta ? (
            <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="tillsyn-current-step">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-blue-700 text-white flex items-center justify-center rounded-sm font-semibold text-sm flex-shrink-0">
                  {step.step}
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-medium text-zinc-900" style={{ fontFamily: "Outfit" }}>{step.meta.question}</h2>
                  {step.meta.help && <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">{step.meta.help}</p>}
                </div>
              </div>
              <div className="mt-5">
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Anteckning (valfritt)</label>
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="tillsyn-note"
                  placeholder="Motivering, datum för kommunicering m.m..."
                  className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm resize-none" />
              </div>
              <div className="mt-5 space-y-2">
                {step.meta.options.map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`step-${step.step}-${opt.value}`}
                    disabled={submitting}
                    onClick={() => decide(opt.value)}
                    className="w-full text-left flex items-center justify-between gap-4 px-4 py-3 border border-zinc-200 rounded-sm hover:border-blue-700 hover:bg-blue-50 transition disabled:opacity-50"
                  >
                    <span className="text-sm font-medium text-zinc-900">{opt.label}</span>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      {opt.tag && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm font-medium">{opt.tag}</span>}
                      {opt.outcome && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-sm">→ slut</span>}
                      {opt.next && <span>→ steg {opt.next}</span>}
                    </div>
                  </button>
                ))}
              </div>
              {submitting && (
                <div className="mt-3 text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Sparar beslut...</div>
              )}
            </div>
          ) : null}
        </div>

        {/* Decision history */}
        <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-sm p-6 h-fit">
          <h3 className="font-medium mb-4" style={{ fontFamily: "Outfit" }}>Beslutshistorik</h3>
          {c.decisions?.length === 0 ? (
            <p className="text-sm text-zinc-500">Inga beslut fattade ännu.</p>
          ) : (
            <ol className="space-y-4">
              {c.decisions.map((d, i) => (
                <li key={i} className="relative pl-6">
                  <span className="absolute left-0 top-1 w-4 h-4 rounded-full bg-blue-700 text-white flex items-center justify-center text-[9px] font-semibold">
                    {d.step}
                  </span>
                  <div className="text-xs text-zinc-500">{formatDateTime(d.at)}</div>
                  <div className="text-sm font-medium text-zinc-900 mt-0.5">{d.label}</div>
                  {d.tag && <span className="inline-block mt-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm">{d.tag}</span>}
                  {d.note && <p className="text-xs text-zinc-600 mt-1 italic">"{d.note}"</p>}
                  <div className="text-[10px] text-zinc-400 mt-0.5">av {d.by}</div>
                </li>
              ))}
            </ol>
          )}

          {c.tags?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-zinc-200">
              <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Vidtagna åtgärder
              </div>
              <div className="flex flex-wrap gap-1.5">
                {c.tags.map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-zinc-100 text-zinc-700 rounded-sm">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fältinspektion: foton, kryssrutor, anteckningar, sanktion */}
      <div className="mt-8">
        <TillsynInspection tillsynId={tillsyn_id} tillsyn={c} />
      </div>

      {pdfOpen && (
        <PdfModal
          tillsyn={c}
          handlaggareName={user?.name}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </div>
  );
}

function PdfModal({ tillsyn, handlaggareName, onClose }) {
  const initialKind = inferDocumentKind(tillsyn);
  const [form, setForm] = useState({
    kind: initialKind,
    vite_amount: "",
    frist_date: "",
    handlaggare_name: handlaggareName || "",
    municipality: "Stockholms stad",
    notes: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const showVite = form.kind === "forelaggande_vite";
  const showFrist = form.kind.startsWith("forelaggande") || form.kind.startsWith("forbud");

  const generate = () => {
    exportTillsynPdf(tillsyn, form);
    toast.success("PDF skapad och nedladdad");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-sm p-6 animate-fade-in-up" data-testid="pdf-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>Exportera beslut som PDF</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Beslutstyp</label>
            <select value={form.kind} onChange={set("kind")} data-testid="pdf-kind"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm bg-white outline-none focus:border-blue-700 text-sm">
              <option value="forelaggande">Föreläggande</option>
              <option value="forelaggande_vite">Föreläggande + vite</option>
              <option value="byggsanktionsavgift">Byggsanktionsavgift</option>
              <option value="forbud_arbete">Förbud mot fortsatt arbete</option>
              <option value="forbud_anvandning">Förbud mot användning</option>
              <option value="avskrivning">Avskrivning</option>
              <option value="rattelse">Slutbeslut – rättelse</option>
              <option value="generic">Övrigt beslut</option>
            </select>
            <p className="mt-1 text-[10px] text-zinc-500">Förinställt baserat på beslutshistoriken.</p>
          </div>
          {showVite && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Vitesbelopp (kr)</label>
              <input value={form.vite_amount} onChange={set("vite_amount")} placeholder="t.ex. 50 000"
                data-testid="pdf-vite"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
          )}
          {showFrist && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Frist för rättelse</label>
              <input type="date" value={form.frist_date} onChange={set("frist_date")} data-testid="pdf-frist"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Kommun</label>
            <input value={form.municipality} onChange={set("municipality")} data-testid="pdf-municipality"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Handläggarens namn</label>
            <input value={form.handlaggare_name} onChange={set("handlaggare_name")} data-testid="pdf-name"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Motivering (valfritt)</label>
            <textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="pdf-notes"
              placeholder="Skriv beslutsmotivering, hänvisningar till PBL m.m..."
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm resize-none" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium hover:bg-zinc-100 rounded-sm">Avbryt</button>
          <button onClick={generate} data-testid="pdf-generate"
            className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm inline-flex items-center gap-2">
            <FileText className="w-4 h-4" /> Generera PDF
          </button>
        </div>
      </div>
    </div>
  );
}
